import React, {createContext, useContext, useEffect, useRef, useState} from "react"
import gsap from "gsap";
import {SteppedEase} from "gsap";
import {useGSAP} from "@gsap/react";


export const GameContext = createContext({});

export const SachaState = {
	IDLEING: 1,
	WALKING: 2,
	JUMPING: 3,
};

export const SpeakingHeadTexts = {
	FIRST_SCENE: 1,
	SECOND_SCENE: 2,
	THIRD_SCENE: 3,
	FOURTH_SCENE: 4,
	FIFTH_SCENE: 5,
	SIXTH_SCENE: 6,
}

export const BackgroundAudio = {
	NO_SOUND: 'NO_SOUND',
	FIRST_SCENE: '/sound/first_scene_background_audio.mp3',
	SECOND_SCENE: '/sound/second_scene_background_audio.mp3',
	THIRD_SCENE: '/sound/third_scene_background_audio.mp3',
	FOURTH_SCENE: '/sound/fourth_scene_background_audio.mp3',
	FIFTH_SCENE: '/sound/fifth_scene_background_audio.mp3',
}

export const Objects = {
	COMPASS: 'compass',
	PARCHMENT: 'parchment',
	SWORD: 'sword',
	TORCH: 'torch',
	CHEST: 'chest',
}

const SachaGifImagesNumber = {
	IDLE: 10,
	WALKING: 8,
	JUMPING: 10
}
const GameProvider = ({ children }) => {
	let intervalCloseToTorch = null

	// Refs
	const heroTimelineRef = useRef(null);
	const gameTimelineRef = useRef(null);
	const backgroundAudioRef = useRef(null);
	const walkingAudioRef = useRef(null);
	const closeToObjectAudioRef = useRef(null);
	const torchAudioRef = useRef(null);
	const isScrollingRef = useRef(false)
	const sachaIdleAnimationRef = useRef(null)
	const sachaWalkingAnimationRef = useRef(null)
	const sachaJumpingAnimationRef = useRef(null)

	// State
	const [playHeroAnimation, setPlayHeroAnimation] = useState(false)
	const [isMobile, setIsMobile] = useState()
	const [isPlaying, setIsPlaying] = useState(false);
	const [isRestart, setIsRestart] = useState(false)
	const [sachaState, setSachaState] = useState(SachaState.IDLEING)
	const [speakingHeadText, setSpeakingHeadText] = useState(SpeakingHeadTexts.FIRST_SCENE)
	const [currentAudioBackgroundSrc, setCurrentAudioBackgroundSrc] = useState()
	const [isSoundMuted, setIsSoundMuted] = useState(false)
	const [playAudioOnceSrc, setPlayAudioOnceSrc] = useState()
	const [secondPlayAudioOneSrc, setSecondPlayAudioOneSrc] = useState()
	const [closeToObjects, setCloseToObjects] = useState([])
	const [objectsTaken, setObjectsTaken] = useState([])
	const [isBackward, setIsBackward] = useState(false)
	const [currentBagSection, setCurrentBagSection] = useState('book');
	const [isContactPopupOpen, setIsContactPopupOpen] = useState(false)
	const [isMenuScrolling, setIsMenuScrolling] = useState(false)


	// Player movement
	const [askedSachaState, setAskedSachaState] = useState(SachaState.IDLEING)
	const [shouldWalk, setShouldWalk] = useState(false)
	const [isJumping, setIsJumping] = useState(false)

	//region Resize
	const [size, setSize] = useState({width: 0, height: 0})

	// Update the game timeline on resize
	useEffect(() => {
		const handleResize = () => {
			setSize({
				width: window.innerWidth,
				height: window.innerHeight
			})

			if(window.innerWidth < 768) {
				setIsMobile(true)
			} else {
				setIsMobile(false)
			}
		}

		handleResize()

		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])
	//endregion

	// region Function Audios
	useEffect(() => {
		if(playAudioOnceSrc) {
			playAudioOnce(playAudioOnceSrc)
			setPlayAudioOnceSrc(null)
		}
	}, [playAudioOnceSrc]);

	useEffect(() => {
		if(secondPlayAudioOneSrc) {
			playAudioOnce(secondPlayAudioOneSrc)
			setSecondPlayAudioOneSrc(null)
		}
	}, [secondPlayAudioOneSrc]);

	const playAudioOnce = (src, volume= 1) => {
		window.audios = window.audios || {}

		if(isSoundMuted || isMobile || isMenuScrolling) return

		if(!window.audios[src]) {
			window.audios[src] = new Audio(src);
			window.audios[src].volume = volume;
		}
		window.audios[src].play()
	}

	// region Background audio
	const playBackgroundAudio = (src, volume = 1) => {
		window.audios = window.audios || {}

		if(isMobile) return
		if(src === BackgroundAudio.NO_SOUND && backgroundAudioRef.current) {
			fadeOutAudio(window.audios[backgroundAudioRef.current])
			backgroundAudioRef.current = null
			return
		}

		if(!window.audios[src]) {
			// Create the new Audio
			window.audios[src] = new Audio(src);
			window.audios[src].loop = true;
			window.audios[src].volume = volume;
		}

		window.audios[src].play()
		if(!isSoundMuted) {
			fadeinAudio(window.audios[src], volume);
		}

		// Stop the previous audio
		if (backgroundAudioRef.current) {
			// Slowly fade out the previous audio
			fadeOutAudio(window.audios[backgroundAudioRef.current]);
		}
		backgroundAudioRef.current = src;
	}
	const fadeinAudio = (audio, volumeMax, fadeValue = .2) => {
		audio.fadeInInterval = setInterval(() => {
			if (audio.volume < volumeMax) {
				audio.volume = Math.min(1.0, audio.volume + fadeValue);
			} else {
				clearInterval(audio.fadeInInterval);
				audio.fadeInInterval = null;
			}
		}, 100);
	}
	const fadeOutAudio = (audio, fadeValue=.1) => {
		if(!audio) return
		if (audio.fadeInInterval) {
			clearInterval(audio.fadeInInterval)
		}
		const fadeOutInterval = setInterval(() => {
			if (audio.volume > fadeValue) {
				audio.volume -= fadeValue;
			} else {
				audio.pause();
				clearInterval(fadeOutInterval);
			}
		}, 100);
	}

	useEffect(() => {
		if (currentAudioBackgroundSrc) {
			if(currentAudioBackgroundSrc === BackgroundAudio.FIRST_SCENE) {
				playBackgroundAudio(currentAudioBackgroundSrc, .5)
			} else {
				playBackgroundAudio(currentAudioBackgroundSrc)
			}
		}
	}, [currentAudioBackgroundSrc])
	// endregion

	// region Walking audio
	const playWalkingAudio = () => {
		if (isMobile) return
		if (walkingAudioRef.current) {
			if(isSoundMuted) walkingAudioRef.current.volume = 0.0
			walkingAudioRef.current.play();
		} else {
			walkingAudioRef.current = new Audio('/sound/steps.mp4');
			walkingAudioRef.current.loop = true;
			if(isSoundMuted) walkingAudioRef.current.volume = 0.0
			walkingAudioRef.current.play();
		}
	}
	const pauseWalkingAudio = () => {
		if (walkingAudioRef.current) {
			walkingAudioRef.current.pause();
		}
	}
	// endregion

	// endregion

	//region Close to Object audio
	const playTorchAudio = () => {
		if (torchAudioRef.current) {
			if(isSoundMuted) torchAudioRef.current.volume = 0.0
			torchAudioRef.current.play();
		} else {
			torchAudioRef.current = new Audio('/sound/torch.mp3');
			torchAudioRef.current.loop = true;
			if(isSoundMuted) torchAudioRef.current.volume = 0.0
			torchAudioRef.current.play();
		}
	}
	const pauseTorchAudio = () => {
		if (torchAudioRef.current) {
			torchAudioRef.current.pause();
		}
	}

	const closeToObjectCalculator = (objectId, closeDistance, audioRef, volumeMultiplier = 1) => {
		if(isMobile) return
		const sacha = document.getElementById('sacha');
		const object = document.getElementById(objectId);
		const firstPlan = document.getElementById('first_plan');
		const firstPlanTransform = getComputedStyle(firstPlan).transform

		const sachaOffset = sacha.offsetLeft
		const objectOffset = object.offsetLeft
		const firstPlanOffset = firstPlanTransform !== 'none' ? parseFloat(firstPlanTransform.split(',')[4]) : 0
		const totalWidth = firstPlan.offsetWidth

		// Calculate the percentage of distance between sacha and the object depending of the total width
		const distance = Math.abs(sachaOffset - objectOffset - firstPlanOffset) / totalWidth

		// If the distance is less than 0.1, then the object is close to sacha, so we play the audio
		if(distance < closeDistance && !objectsTaken.includes(objectId)) {
			// Get the volume of the audio depending on the distance
			const volume = Math.max(volumeMultiplier - (distance * (100 * closeDistance)), 0)
			if(!closeToObjects.includes(objectId)) setCloseToObjects([...closeToObjects, objectId])
			if(audioRef.current) {
				audioRef.current.play()
				isSoundMuted ? audioRef.current.volume = 0.0 : audioRef.current.volume = volume
			}
		} else {
			// Call the function again
			if(closeToObjects.includes(objectId)) {
				setCloseToObjects(closeToObjects.filter(item => item !== objectId))
				audioRef.current?.pause()
			}
		}
	}

	const setIntervalCloseToObjects = (object, closeDistance, audioRef, volumeMultiplier) => {
		if(closeToObjects.includes(object)) {
			return setInterval(() => {
				closeToObjectCalculator(object, closeDistance, audioRef, volumeMultiplier);
			}, 250);
		} else {
			return setInterval(() => {
				closeToObjectCalculator(object, closeDistance, audioRef, volumeMultiplier);
			}, 1000);
		}
	}

	// Close to object audio Calculation
	useEffect(() => {
		if(isPlaying) {
			// Initialize the audios
			!isMobile && playTorchAudio()
			pauseTorchAudio()

			intervalCloseToTorch = setIntervalCloseToObjects(Objects.TORCH, .1, torchAudioRef, 1)

			return () => {
				clearInterval(intervalCloseToTorch)
			}
		}
	}, [isPlaying, closeToObjects, isSoundMuted, objectsTaken, isMobile])
	//endregion

	// Mute or unmute the sound
	useEffect(() => {
		if(isSoundMuted) {
			// Mute all the current sound playing in the game by setting the volume to 0
			if (backgroundAudioRef.current) {
				window.audios[backgroundAudioRef.current].volume = 0;
			}
			if (walkingAudioRef.current) {
				walkingAudioRef.current.volume = 0;
			}
			if(closeToObjectAudioRef.current) {
				closeToObjectAudioRef.current.volume = 0;
			}
			if(torchAudioRef.current) {
				torchAudioRef.current.volume = 0;
			}
		} else {
			// Unmute all the current sound playing in the game by setting the volume to 1
			if (backgroundAudioRef.current) {
				window.audios[backgroundAudioRef.current].volume = 1;
			}
			if (walkingAudioRef.current) {
				walkingAudioRef.current.volume = 1;
			}
			if(closeToObjectAudioRef.current) {
				closeToObjectAudioRef.current.volume = 1;
			}
			if(torchAudioRef.current) {
				torchAudioRef.current.volume = 1;
			}
		}
	}, [isSoundMuted]);

	// region Sacha's state effect
	// Create the gif animations
	useGSAP(() => {
		// Get the container width and the idle gif image width
		const containerWidth = document.querySelector('.sacha__wrapper').getBoundingClientRect().width

		const baseWalkingImageSizeWidth = 64
		const walkingContainerWidthOffset = 4 * (containerWidth / baseWalkingImageSizeWidth)

		// Get the width of the images
		const idleImageWidth = containerWidth * (SachaGifImagesNumber.IDLE - 1)
		const walkingImageWidth = (containerWidth + walkingContainerWidthOffset) * (SachaGifImagesNumber.WALKING - 1)
		const jumpingImageWidth = containerWidth * (SachaGifImagesNumber.JUMPING - 1)

		// Create the animations
		if(!isMobile) {
			sachaIdleAnimationRef.current = gsap.timeline({repeat: -1})
					.to('.gsap-sacha_ideling', {
						x: -idleImageWidth,
						ease: SteppedEase.config(SachaGifImagesNumber.IDLE - 1)
					}).timeScale(.25)
		}

		sachaWalkingAnimationRef.current = gsap.timeline({})
				.to('.gsap-sacha_walking', {
					x: -walkingImageWidth,
					ease: SteppedEase.config(SachaGifImagesNumber.WALKING - 1)
				}).timeScale(.5)

		sachaJumpingAnimationRef.current = gsap.timeline({})
				.to('.gsap-sacha_jumping', {
					x: -jumpingImageWidth,
					ease: SteppedEase.config(SachaGifImagesNumber.JUMPING - 1)
				}).timeScale(.5)
	}, {dependencies: [size], revertOnUpdate: true});

	// Handle the change of states
	useEffect(() => {
		switch (askedSachaState) {
			case SachaState.IDLEING:
				if(!isJumping) {
					setSachaState(SachaState.IDLEING)
					setIsJumping(false)
				}
				break;
			case SachaState.JUMPING:
				setSachaState(SachaState.JUMPING)
				setIsJumping(true)
				break;
			case SachaState.WALKING:
				if(shouldWalk){
					setSachaState(SachaState.WALKING)
					setIsJumping(false)
				} else {
					if(sachaState !== SachaState.IDLEING) setAskedSachaState(SachaState.IDLEING)
				}
				break;
		}
	}, [askedSachaState, shouldWalk, isJumping])

	useEffect(() => {
		switch (sachaState) {
			case SachaState.IDLEING:
				pauseWalkingAudio()
				setIsJumping(false)
				break;
			case SachaState.WALKING:
				playWalkingAudio()
				setIsJumping(false)
				break;
			case SachaState.JUMPING:
				pauseWalkingAudio()
				playAudioOnce('/sound/jump.mp4')
				setIsJumping(true)
				break;
		}
	}, [sachaState, isSoundMuted])
	// endregion

	//region Contact Popup
	useEffect(() => {
		isPlaying ? (isContactPopupOpen ? document.body.classList.add('no_scroll') : document.body.classList.remove('no_scroll')) : null
	}, [isContactPopupOpen])
	//endregion

	return (<GameContext.Provider value={{
				// Refs
				heroTimelineRef, gameTimelineRef, isScrollingRef,
				sachaIdleAnimationRef, sachaWalkingAnimationRef, sachaJumpingAnimationRef,

				// States
				playHeroAnimation, setPlayHeroAnimation,
				size, setSize, isMobile, setIsMobile,
				isPlaying, setIsPlaying,
				isRestart, setIsRestart,
				sachaState, setSachaState,
				speakingHeadText, setSpeakingHeadText,
				currentAudioBackgroundSrc, setCurrentAudioBackgroundSrc,
				isSoundMuted, setIsSoundMuted,
				playAudioOnceSrc, setPlayAudioOnceSrc,
				secondPlayAudioOneSrc, setSecondPlayAudioOneSrc,
				objectsTaken, setObjectsTaken,
				isBackward, setIsBackward,
				shouldWalk, setShouldWalk,
				askedSachaState, setAskedSachaState,
				isJumping, setIsJumping,
				currentBagSection, setCurrentBagSection,
				isContactPopupOpen, setIsContactPopupOpen,
				isMenuScrolling, setIsMenuScrolling,


				// Functions
				playAudioOnce
	}}>
	  {children}
	</GameContext.Provider>
  );
}

export default GameProvider;

// Hook
export const useGameContext = () => {
	  const context = useContext(GameContext);
  if (!context) {
	throw new Error("useGameContext must be used within a GameProvider");
  }
  return context;
}
